import "./OtaExplorer.scss";
import { useState, useEffect, useCallback, useMemo } from "react";
import { notification, Button, Space, Tag, Tree, Empty, Dropdown, Tooltip, Typography, Spin, Input, Breadcrumb, Pagination, Segmented } from "antd";
import {
  FolderOutlined, FolderOpenOutlined, DeploymentUnitOutlined, CheckCircleFilled,
  DownloadOutlined, MoreOutlined, DeleteOutlined, ReloadOutlined, SearchOutlined,
  RightOutlined, HddOutlined, ArrowUpOutlined, UnorderedListOutlined, AppstoreOutlined, PlusOutlined,
  SwapOutlined, DesktopOutlined
} from "@ant-design/icons";
import { hasPrivilege } from "../../api/token.js";
import service from "../../services/Service.js";
import { withLocale } from "../../l10n/index.jsx";
import DataTable from "../DataTable/index.js";
import OtaUploadModal from "../OtaUploadModal";
import OtaDeleteFolderModal from "../OtaDeleteFolderModal";
import OtaAudienceModal from "../OtaAudienceModal";
import { OTA_VERSIONS_VIEW, OTA_VERSIONS_PAGE_SIZE, OTA_VERSION_CARDS_PAGE_SIZE } from "../../utils/constants";
import { formatDate, formatDateTime } from '../../utils/dates';

const { Text, Title } = Typography;

// Funciones puras sobre el árbol, no dependen del estado.

/** Un nodo se mantiene visible si alguno de sus descendientes casa con la búsqueda. */
function hasMatch(node, term) {
  if (!term) return true;
  return (node.children ?? []).some(child =>
    child.name.toLowerCase().includes(term.toLowerCase()) || hasMatch(child, term)
  );
}

/** Busca un nodo del árbol por su ruta completa. */
function findNode(nodes, target) {
  if (!target) return null;

  for (const node of nodes) {
    if (node.path === target) return node;

    const found = findNode(node.children ?? [], target);
    if (found) return found;
  }

  return null;
}

/** Todas las rutas del árbol, para expandirlo entero al buscar. */
function allPaths(nodes) {
  return nodes.flatMap(node => [node.path, ...allPaths(node.children ?? [])]);
}

/**
 * Gestión del firmware publicado en el servicio OTA.
 *
 * A la izquierda el árbol de firmwares, sus canales y las variantes que cuelguen de
 * ellos; a la derecha el contenido de lo seleccionado: las carpetas que tenga dentro,
 * las versiones que contenga, o ambas cosas. El vocabulario esconde la estructura de
 * carpetas del almacén: firmware, canal y variante.
 */
function OtaExplorer({ lang }) {

  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({
    loadingTree: false,
    loadingContent: false,
    uploadVisible: false,
    deleteVisible: false,
    audienceVisible: false,
    uploadIntent: 'version',
  });

  const [tree, setTree] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState([]);
  const [content, setContent] = useState({ versions: [], latest: null });
  // Versión cuyo destino se está cambiando; null significa que se cambia el del firmware
  const [audienceVersion, setAudienceVersion] = useState(null);
  // Versión pendiente de confirmar su borrado; null significa que se borra la carpeta abierta
  const [deleteVersionRecord, setDeleteVersionRecord] = useState(null);
  const [versionPage, setVersionPage] = useState(1);
  const [versionFilter, setVersionFilter] = useState('');

  // La forma de ver las versiones la elige el usuario y se recuerda entre sesiones
  const [viewMode, setViewMode] = useState(() => localStorage.getItem(OTA_VERSIONS_VIEW) === 'cards' ? 'cards' : 'table');

  // ===== 2. SELECTORES / CONSTANTES =====
  const ota_edit = hasPrivilege('ota_edit');

  // ===== 3. DERIVADOS =====
  const path = useMemo(() => selected ? selected.split('/') : [], [selected]);
  const firmware = path[0] ?? null;
  const channelName = path[1] ?? null;
  const variant = path.length > 2 ? path.slice(2).join('/') : null;

  const insideChannel = Boolean(firmware && channelName);

  // Las carpetas a la vista salen del árbol, que ya sabe lo que hay dentro de cada una:
  // sin selección son los firmwares, y dentro de uno, lo que cuelgue de él.
  const folders = useMemo(() => {
    if (!selected) return tree;

    return findNode(tree, selected)?.children ?? [];
  }, [tree, selected]);

  const hasFolders = folders.length > 0;
  const hasVersions = content.versions.length > 0;

  // Los filtros trabajan sobre las versiones de lo que hay abierto, nada más
  const filteredVersions = useMemo(() => {
    const term = versionFilter.trim().toLowerCase();

    if (!term) return content.versions;

    return content.versions.filter(item => item.version.toLowerCase().includes(term));
  }, [content.versions, versionFilter]);

  // En tarjetas la paginación se lleva a mano; en tabla la gestiona el propio Table
  const pagedVersions = useMemo(() => {
    const from = (versionPage - 1) * OTA_VERSION_CARDS_PAGE_SIZE;
    return filteredVersions.slice(from, from + OTA_VERSION_CARDS_PAGE_SIZE);
  }, [filteredVersions, versionPage]);

  // ===== 4. HELPERS =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));

  const notifyError = useCallback((message) => {
    notification["error"]({ message: lang.handle(message) });
  }, [lang]);

  const formatSize = (bytes) => bytes
    ? bytes >= 1024 * 1024
      ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
      : `${Math.max(1, Math.round(bytes / 1024))} KB`
    : '—';

  /** Nodos para el árbol lateral, con el recuento de versiones de cada rama. */
  const toTreeData = useCallback((nodes, depth = 0) => nodes
    .filter(node => !search || node.name.toLowerCase().includes(search.toLowerCase()) || hasMatch(node, search))
    .map(node => ({
      key: node.path,
      title: (
        <span className="ota-tree__node">
          <span className="ota-tree__name">{node.name}</span>
          {node.has_versions &&
            <span className="ota-tree__count">{node.versions_count}</span>
          }
        </span>
      ),
      icon: depth === 0
        ? <HddOutlined />
        : node.has_versions ? <DeploymentUnitOutlined /> : <FolderOutlined />,
      children: node.children?.length ? toTreeData(node.children, depth + 1) : undefined,
    })), [search]);

  /** Acciones de una versión, compartidas por la tabla y las tarjetas. */
  const actionItems = (record) => {
    const items = [{
      key: 'download',
      icon: <DownloadOutlined />,
      label: lang.handle('Descargar'),
      onClick: () => download(record)
    }];

    if (ota_edit && !record.is_latest) {
      items.push({
        key: 'latest',
        icon: <CheckCircleFilled />,
        label: lang.handle('Poner en distribución'),
        onClick: () => markAsLatest(record.version)
      });
    }

    if (ota_edit) {
      items.push({
        key: 'audience',
        icon: <SwapOutlined />,
        label: lang.handle('Cambiar destino'),
        onClick: () => openVersionAudience(record)
      });
    }

    if (ota_edit) {
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        danger: true,
        label: record.is_latest ? lang.handle('Eliminar y retirar de distribución') : lang.handle('Eliminar'),
        onClick: () => removeVersion(record)
      });
    }

    return items;
  };

  /**
   * Distintivo del destino de una versión: a quién va dirigida y, por tanto, qué se
   * comprueba antes de entregarla. Sin declarar son las publicadas antes de que existiera
   * la distinción, que el servicio OTA trata como dirigidas a equipos.
   */
  const audienceTag = (value) => {
    if (value === 'user') {
      return (
        <Tooltip title={lang.handle('Se descarga sin comprobar nada en Core, porque no hay equipo que comprobar')}>
          <Tag icon={<DesktopOutlined />} color="blue" bordered={false}>{lang.handle('Aplicación de usuario')}</Tag>
        </Tooltip>
      );
    }

    if (value === 'device') {
      return (
        <Tooltip title={lang.handle('Core comprueba que el equipo esté registrado y con la suscripción en regla')}>
          <Tag icon={<HddOutlined />} bordered={false}>{lang.handle('Equipos')}</Tag>
        </Tooltip>
      );
    }

    return (
      <Tooltip title={lang.handle('Se publicó antes de declarar destino: se trata como dirigida a equipos')}>
        <Tag bordered={false} className="ota-versions__audience--none">{lang.handle('Sin declarar')}</Tag>
      </Tooltip>
    );
  };

  /** Qué es una carpeta según su profundidad, con el mismo vocabulario del lateral. */
  const folderKind = (folderPath) => {
    const depth = folderPath.split('/').length;

    if (depth === 1) return lang.handle('Firmware');
    if (depth === 2) return lang.handle('Canal');
    return lang.handle('Variante');
  };

  /** Resumen de lo que contiene una carpeta: sus versiones, o las carpetas que agrupa. */
  const folderMeta = (folder) => {
    if (folder.has_versions) {
      const count = folder.versions_count ?? 0;
      return `${count} ${count === 1 ? lang.handle('versión') : lang.handle('versiones')}`;
    }

    const count = folder.children?.length ?? 0;
    const isFirmware = folder.path.split('/').length === 1;

    const word = isFirmware
      ? (count === 1 ? lang.handle('canal') : lang.handle('canales'))
      : (count === 1 ? lang.handle('variante') : lang.handle('variantes'));

    return `${count} ${word}`;
  };

  // ===== 5. FUNCIONES PRINCIPALES =====
  const fetchTree = useCallback(async () => {
    try {
      const res = await service.get("ota/tree");

      if (!res || res.data.ok === false) {
        notifyError(res?.data?.message || "Error al obtener datos");
        return;
      }

      setTree(res.data.data.tree ?? []);
    } catch (err) {
      console.error("Error devuelto: " + err);
    }
  }, [notifyError]);

  const fetchContent = useCallback(async () => {
    if (!firmware || !channelName) {
      setContent({ versions: [], latest: null });
      return;
    }

    try {
      const params = { product: firmware, channel: channelName };
      if (variant) params.target = variant;

      const res = await service.get("ota/versions", params);

      if (!res || res.data.ok === false) {
        notifyError(res?.data?.message || "Error al obtener datos");
        return;
      }

      const { versions = [], latest = null } = res.data.data;
      setContent({ versions, latest });
    } catch (err) {
      console.error("Error devuelto: " + err);
    }
  }, [firmware, channelName, variant, notifyError]);

  const reload = useCallback(async () => {
    updateUI('loadingContent', true);
    await Promise.all([fetchTree(), fetchContent()]);
    updateUI('loadingContent', false);
  }, [fetchTree, fetchContent]);

  // ===== 6. HANDLERS =====
  const openFolder = (folderPath) => {
    setSelected(folderPath);
    setExpanded(prev => prev.includes(folderPath) ? prev : [...prev, folderPath]);
  };

  const markAsLatest = async (version) => {
    updateUI('loadingContent', true);
    try {
      const body = { version };
      if (variant) body.target = variant;

      const res = await service.put(`ota/versions/${firmware}/${channelName}/latest`, body);
      if (!res) return;

      if (res.data.ok === false) {
        notifyError(res.data.message);
      } else {
        notification["success"]({ message: lang.handle('Versión marcada como latest') });
      }

      await fetchContent();
    } catch (err) {
      console.error("Error devuelto: " + err);
    } finally {
      updateUI('loadingContent', false);
    }
  };

  /** Borrar no tiene vuelta atrás: se confirma en el mismo modal que el de las carpetas. */
  const removeVersion = (record) => {
    setDeleteVersionRecord(record);
    updateUI('deleteVisible', true);
  };

  const openFolderDelete = () => {
    setDeleteVersionRecord(null);
    updateUI('deleteVisible', true);
  };

  const closeDelete = () => {
    updateUI('deleteVisible', false);
    setDeleteVersionRecord(null);
  };

  const download = async (record) => {
    const query = variant ? `?target=${encodeURIComponent(variant)}` : '';
    const filename = record.file?.name ?? `${firmware}-${record.version}`;

    await service.download(`ota/versions/${firmware}/${channelName}/${record.version}/download${query}`, filename);
  };

  const onUploaded = async () => {
    updateUI('uploadVisible', false);
    await reload();
  };

  const closeUpload = () => {
    updateUI('uploadVisible', false);
  };

  const openUpload = (intent = 'version') => {
    setUI(prev => ({ ...prev, uploadIntent: intent, uploadVisible: true }));
  };

  /** Tras borrar una carpeta hay que salir de ella: ya no existe. */
  const openVersionAudience = (record) => {
    setAudienceVersion(record);
    updateUI('audienceVisible', true);
  };

  const closeAudience = () => {
    updateUI('audienceVisible', false);
    setAudienceVersion(null);
  };

  const onAudienceChanged = async () => {
    closeAudience();
    await reload();
  };

  const onDeleted = async () => {
    const wasFolder = !deleteVersionRecord;
    closeDelete();

    // La carpeta abierta ya no existe: hay que salir de ella
    if (wasFolder) setSelected(path.length > 1 ? path.slice(0, -1).join('/') : null);

    await reload();
  };

  // Dentro de un firmware se crean canales; dentro de un canal, versiones.
  const creatableKind = !selected ? 'firmware' : path.length === 1 ? 'channel' : 'version';

  const creatableLabel = {
    firmware: lang.handle('Nuevo firmware'),
    channel: lang.handle('Nuevo canal'),
    version: lang.handle('Nueva versión'),
  }[creatableKind];

  // Qué es lo que hay abierto, para nombrarlo al borrarlo
  const currentKind = path.length === 1 ? 'firmware' : path.length === 2 ? 'channel' : 'variant';

  const currentKindLabel = {
    firmware: lang.handle('el firmware'),
    channel: lang.handle('el canal'),
    variant: lang.handle('la variante'),
  }[currentKind];

  // ===== 7. EFFECTS =====
  useEffect(() => {
    updateUI('loadingTree', true);
    fetchTree().then(() => updateUI('loadingTree', false));
  }, [fetchTree]);

  useEffect(() => {
    updateUI('loadingContent', true);
    fetchContent().then(() => updateUI('loadingContent', false));
  }, [fetchContent]);

  // Al cambiar de carpeta se empieza de cero: ni página heredada ni filtro previo
  useEffect(() => {
    setVersionPage(1);
    setVersionFilter('');
  }, [selected]);

  // Filtrar deja fuera resultados: volver a la primera página evita quedarse en una vacía
  useEffect(() => {
    setVersionPage(1);
  }, [versionFilter, viewMode]);

  useEffect(() => {
    localStorage.setItem(OTA_VERSIONS_VIEW, viewMode);
  }, [viewMode]);

  // ===== 8. CONFIG =====
  const treeData = useMemo(() => toTreeData(tree), [tree, toTreeData]);

  // El rastro arranca siempre en la raíz, para poder volver a ella desde cualquier nivel
  const breadcrumbItems = useMemo(() => [
    {
      title: path.length === 0
        ? <span className="ota-crumb ota-crumb--current">{lang.handle('Firmwares')}</span>
        : <button className="ota-crumb" onClick={() => setSelected(null)}>{lang.handle('Firmwares')}</button>
    },
    ...path.map((segment, index) => ({
      title: index === path.length - 1
        ? <span className="ota-crumb ota-crumb--current">{segment}</span>
        : <button className="ota-crumb" onClick={() => setSelected(path.slice(0, index + 1).join('/'))}>{segment}</button>
    }))
  ], [path, lang]);

  /** Sube un nivel: al padre de lo seleccionado, o a la raíz si ya es de primer nivel. */
  const goUp = () => setSelected(path.length > 1 ? path.slice(0, -1).join('/') : null);

  const columns = [
    {
      title: lang.handle('Versión'),
      dataIndex: 'version',
      key: 'version',
      render: (version, record) => (
        <Space size={8}>
          {record.is_latest && <CheckCircleFilled className="ota-versions__live-dot" />}
          <Text strong={record.is_latest}>{version}</Text>
          {record.is_latest && <Tag color="success" bordered={false}>{lang.handle('En distribución')}</Tag>}
        </Space>
      )
    },
    {
      title: lang.handle('Publicada'),
      dataIndex: 'release_date',
      key: 'release_date',
      render: (date) => date
        ? <Tooltip title={formatDateTime(date)}>
          <Text type="secondary">{formatDate(date)}</Text>
        </Tooltip>
        : '—'
    },
    {
      title: lang.handle('Publicada por'),
      dataIndex: 'release_by',
      key: 'release_by',
      render: (value) => value ?? '—'
    },
    {
      title: lang.handle('Tamaño'),
      dataIndex: 'file',
      key: 'size',
      render: (file) => <Text type="secondary">{formatSize(file?.size)}</Text>
    },
    {
      title: 'SHA-256',
      dataIndex: 'checksum',
      key: 'checksum',
      render: (checksum) => checksum
        ? <Tooltip title={checksum}><Text code className="ota-versions__checksum">{checksum.slice(0, 10)}</Text></Tooltip>
        : '—'
    },
    {
      title: lang.handle('Destino'),
      dataIndex: 'audience',
      key: 'audience',
      render: (value) => audienceTag(value)
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      align: 'right',
      render: (_, record) => (
        <Space size={0}>
          <Tooltip title={lang.handle('Descargar')}>
            <Button type="text" icon={<DownloadOutlined />} onClick={() => download(record)} />
          </Tooltip>
          <Dropdown
            menu={{ items: actionItems(record).filter(item => item.key !== 'download') }}
            trigger={['click']}
            placement="bottomRight"
            disabled={!ota_edit}
          >
            <Button type="text" icon={<MoreOutlined />} disabled={!ota_edit} />
          </Dropdown>
        </Space>
      )
    }
  ];

  // ===== 9. RENDER =====
  return (
    <div className="ota-explorer">
      <aside className="ota-explorer__sidebar">
        <div className="ota-sidebar__head">
          <span className="ota-sidebar__label">{lang.handle('Firmwares')}</span>
          <Tooltip title={lang.handle('Actualizar')}>
            <Button type="text" size="small" icon={<ReloadOutlined />} onClick={reload} />
          </Tooltip>
        </div>

        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined />}
          placeholder={lang.handle('Buscar')}
          className="ota-sidebar__search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="ota-sidebar__body">
          {ui.loadingTree
            ? <div className="ota-explorer__centered"><Spin /></div>
            : treeData.length === 0
              ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={lang.handle('Sin firmwares publicados')} />
              : <Tree
                showIcon
                blockNode
                treeData={treeData}
                selectedKeys={selected ? [selected] : []}
                expandedKeys={search ? allPaths(tree) : expanded}
                onExpand={(keys) => setExpanded(keys)}
                onSelect={(keys) => keys[0] && setSelected(keys[0])}
                className="ota-tree"
              />
          }
        </div>
      </aside>

      <section className="ota-explorer__detail">
        {ui.loadingTree && !selected
          ? <div className="ota-explorer__centered"><Spin /></div>
          : <>
            <header className="ota-detail__head">
              <div className="ota-detail__heading">
                <Breadcrumb items={breadcrumbItems} separator={<RightOutlined className="ota-crumb__sep" />} />

                <div className="ota-detail__titleline">
                  {selected &&
                    <Tooltip title={lang.handle('Subir un nivel')}>
                      <Button
                        type="text"
                        size="small"
                        className="ota-detail__up"
                        icon={<ArrowUpOutlined />}
                        onClick={goUp}
                      />
                    </Tooltip>
                  }
                  <Title level={4} className="ota-detail__title">
                    {selected ? path[path.length - 1] : lang.handle('Firmwares')}
                  </Title>

                  <span className="ota-detail__status">
                    {content.latest
                      ? <>
                        <CheckCircleFilled className="ota-versions__live-dot" />
                        {lang.handle('En distribución')} <span className="ota-detail__status-value">{content.latest}</span>
                      </>
                      : insideChannel && hasVersions
                        ? lang.handle('Ninguna versión en distribución')
                        : folderMeta({
                          path: selected ?? '',
                          children: folders,
                          has_versions: false
                        })
                    }
                  </span>
                </div>
              </div>

              {ota_edit &&
                <Space>
                  <Button icon={<PlusOutlined />} onClick={() => openUpload(creatableKind)}>
                    {creatableLabel}
                  </Button>

                  {selected &&
                    <Dropdown
                      trigger={['click']}
                      placement="bottomRight"
                      menu={{
                        items: [
                          // El destino es del software entero, así que solo se ofrece sobre el firmware
                          ...(currentKind === 'firmware' ? [{
                            key: 'audience',
                            icon: <SwapOutlined />,
                            label: lang.handle('Cambiar destino'),
                            onClick: () => updateUI('audienceVisible', true)
                          }, { type: 'divider' }] : []),
                          {
                            key: 'delete-folder',
                            icon: <DeleteOutlined />,
                            danger: true,
                            label: `${lang.handle('Eliminar')} ${currentKindLabel}`,
                            onClick: () => openFolderDelete()
                          }
                        ]
                      }}
                    >
                      <Button icon={<MoreOutlined />} />
                    </Dropdown>
                  }
                </Space>
              }
            </header>

            {hasFolders &&
              <section className="ota-folders">
                <div className="ota-folders__grid">
                  {folders.map(folder => (
                    <button
                      key={folder.path}
                      className={`ota-folder ${folder.has_versions ? 'ota-folder--versions' : ''}`}
                      onClick={() => openFolder(folder.path)}
                    >
                      <span className="ota-folder__icon">
                        {folder.path.split('/').length === 1
                          ? <HddOutlined />
                          : folder.has_versions ? <DeploymentUnitOutlined /> : <FolderOpenOutlined />}
                      </span>
                      <span className="ota-folder__text">
                        <span className="ota-folder__kind">{folderKind(folder.path)}</span>
                        <span className="ota-folder__name">{folder.name}</span>
                        <span className="ota-folder__meta">{folderMeta(folder)}</span>
                      </span>
                      <RightOutlined className="ota-folder__chevron" />
                    </button>
                  ))}
                </div>

              </section>
            }

            {!hasFolders && !hasVersions &&
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={insideChannel ? lang.handle('Sin versiones publicadas') : lang.handle('Sin firmwares publicados')}
              />
            }

            {hasVersions &&
              <section className="ota-versions">
                {hasFolders && <h4 className="ota-section__title">{lang.handle('Versiones')}</h4>}

                <div className="ota-versions__toolbar">
                  <Input
                    allowClear
                    prefix={<SearchOutlined />}
                    placeholder={lang.handle('Buscar versión')}
                    className="ota-versions__search"
                    value={versionFilter}
                    onChange={(e) => setVersionFilter(e.target.value)}
                  />

                  <Segmented
                    value={viewMode}
                    onChange={setViewMode}
                    options={[
                      { value: 'table', icon: <UnorderedListOutlined />, title: lang.handle('Ver como tabla') },
                      { value: 'cards', icon: <AppstoreOutlined />, title: lang.handle('Ver como tarjetas') },
                    ]}
                  />
                </div>

                {filteredVersions.length === 0
                  ? <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={lang.handle('Ninguna versión coincide con la búsqueda')}
                  />
                  : viewMode === 'table'
                    ? <DataTable
                      columns={columns}
                      dataSource={filteredVersions}
                      rowKey="version"
                      loading={ui.loadingContent}
                      scroll={null}
                      pagination={{ pageSize: OTA_VERSIONS_PAGE_SIZE, showSizeChanger: false }}
                      rowClassName={(record) => record.is_latest ? 'ota-versions__row--live' : ''}
                    />
                    : <>
                      <div className="ota-versions__grid">
                        {pagedVersions.map(record => (
                          <article
                            key={record.version}
                            className={`ota-version ${record.is_latest ? 'ota-version--live' : ''}`}
                          >
                            <header className="ota-version__head">
                              <span className="ota-version__name">{record.version}</span>
                              {record.is_latest &&
                                <Tag color="success" bordered={false}>{lang.handle('En distribución')}</Tag>
                              }
                              <Dropdown menu={{ items: actionItems(record) }} trigger={['click']} placement="bottomRight">
                                <Button type="text" size="small" icon={<MoreOutlined />} />
                              </Dropdown>
                            </header>

                            <p className="ota-version__meta">
                              {formatDate(record.release_date) || '—'}
                              <span className="ota-version__dot">·</span>
                              {formatSize(record.file?.size)}
                              <span className="ota-version__dot">·</span>
                              {record.release_by ?? '—'}
                            </p>

                            <div className="ota-version__audience">{audienceTag(record.audience)}</div>

                            {record.checksum &&
                              <Tooltip title={record.checksum}>
                                <Text code className="ota-versions__checksum">{record.checksum.slice(0, 16)}</Text>
                              </Tooltip>
                            }
                          </article>
                        ))}
                      </div>

                      {filteredVersions.length > OTA_VERSION_CARDS_PAGE_SIZE &&
                        <Pagination
                          className="ota-versions__pagination"
                          align="end"
                          size="small"
                          current={versionPage}
                          pageSize={OTA_VERSION_CARDS_PAGE_SIZE}
                          total={filteredVersions.length}
                          onChange={setVersionPage}
                          showSizeChanger={false}
                        />
                      }
                    </>
                }
              </section>
            }

          </>
        }
      </section>

      <OtaUploadModal
        open={ui.uploadVisible}
        product={firmware}
        channel={channelName}
        target={variant}
        tree={tree}
        existingVersions={content.versions.map(item => item.version)}
        intent={ui.uploadIntent}
        onCancel={closeUpload}
        onUploaded={onUploaded}
      />

      <OtaDeleteFolderModal
        open={ui.deleteVisible}
        path={selected ?? ''}
        kind={currentKind}
        version={deleteVersionRecord}
        onCancel={closeDelete}
        onDeleted={onDeleted}
      />

      <OtaAudienceModal
        open={ui.audienceVisible}
        product={firmware ?? ''}
        channel={audienceVersion ? channelName : null}
        target={audienceVersion ? variant : null}
        version={audienceVersion?.version ?? null}
        current={audienceVersion ? audienceVersion.audience : undefined}
        isLatest={audienceVersion?.is_latest ?? false}
        onCancel={closeAudience}
        onChanged={onAudienceChanged}
      />
    </div>
  );
}

export default withLocale(OtaExplorer);
